import { graphql } from "@acto/gql";
import { useMemo, useRef } from "react";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Customer } from "@acto/taskpane/core/domain";
import { yearToDateRange } from "@acto/web-common/dates";
import { ClientFilter, ClientTrendFilter, KpiComparisonMode } from "@acto/gql/graphql";
import { mapClientToCustomer } from "./mapClientToCustomer";

const RankedClientsWithKpisQuery = graphql(`
  query GetRankedClientsWithKpis(
    $first: Int!
    $after: String
    $filter: ClientFilter
    $dateRange: DateRangeInput!
    $comparisonMode: KpiComparisonMode!
  ) {
    rankedClients(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
          revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
          profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
      dateRanges {
        current {
          startDate
          endDate
        }
      }
    }
  }
`);

/** The resolved KPI window the backend computed the client KPIs over. */
export interface RevenuePeriod {
  start: Date;
  end: Date;
}

interface Options {
  limit?: number;
  nameStartsWithOrExternalReference?: string;
  focusOnly?: boolean;
  trends?: ClientTrendFilter[];
  enabled?: boolean;
}

export function useRankedClientsWithKpis({
  limit = 10,
  nameStartsWithOrExternalReference,
  focusOnly,
  trends,
  enabled = true,
}: Options = {}): PaginatedDataAccessResult<Customer> & { revenuePeriod: RevenuePeriod | null } {
  // Year-to-date, the default window everywhere else in field-sales-2, so a card's delta and
  // the customer's own trend cover the same span. Revenue is near-zero in early January.
  // The same window feeds the server-side trend filter and the per-client KPI arguments, so
  // the delta and the Wachstum/Rückgang gate agree.
  const variables = useMemo(() => {
    const dateRange = yearToDateRange();
    // Year to date compares against last year whatever the tenant configured.
    const comparisonMode = KpiComparisonMode.YearOverYear;
    const filter: ClientFilter = {
      dateRange,
      comparisonMode,
      ...(nameStartsWithOrExternalReference ? { nameStartsWithOrExternalReference } : {}),
      // recommendedOnly is the API name; focusOnly stays the UI-facing option.
      ...(focusOnly ? { recommendedOnly: true } : {}),
      ...(trends && trends.length > 0 ? { trends } : {}),
    };
    return { filter, dateRange, comparisonMode };
  }, [nameStartsWithOrExternalReference, focusOnly, trends]);

  // Connection-level metadata rides along outside usePagination's node
  // accumulation: the callback stashes the backend-resolved KPI window here.
  // The same variables always resolve to the same window (modulo a midnight
  // rollover), so last-write-wins across pages is safe.
  const revenuePeriodRef = useRef<RevenuePeriod | null>(null);

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: RankedClientsWithKpisQuery,
    limit,
    variables,
    enabled,
    connection: (data) => {
      const current = data.rankedClients.dateRanges?.current;
      revenuePeriodRef.current = current
        ? { start: new Date(current.startDate), end: new Date(current.endDate) }
        : null;
      return data.rankedClients;
    },
  });

  const customers = useMemo(() => data?.map((node) => mapClientToCustomer(node)), [data]);

  return {
    data: customers,
    revenuePeriod: revenuePeriodRef.current,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
